// IndexedDB-backed buffer for recording chunks. Each chunk is a Blob keyed
// by (sessionId, seq). 5.10.4 (resumable upload) reads from here on
// reconnect to find chunks the server hasn't acknowledged yet.
//
// We deliberately avoid an external IDB wrapper — the API surface is tiny
// (put, get, list, delete) so a 100-line hand-rolled module is the right
// trade-off vs. another dep.

// IDB version bump: v2 introduces (sessionId, callId, seq) composite key so
// each call within a session is independently addressable. Existing v1 data
// (single call per session) is silently dropped via createObjectStore in
// `onupgradeneeded` — acceptable for dev/POC since the server-side is the
// source of truth for uploaded chunks.
const DB_NAME = 'openline.recordings.v2';
const STORE = 'chunks';

export interface ChunkRow {
  sessionId: string;
  callId: string;
  seq: number;
  blob: Blob;
  recordedAt: number;
  /** Wall-clock start time of THIS call — same value on every chunk of the
   *  call. The server uses the chunk-0 value as the canonical call start. */
  callStartedAt: number;
  /** Marked when the server has 204'd this chunk; eligible for deletion. */
  uploaded?: boolean;
}

let dbPromise: Promise<IDBDatabase> | null = null;

/** Eager open. Call at recording start so the very first putChunk doesn't
 *  wait on the IDB upgrade transaction — the upgrade can take 50-200 ms
 *  on first use, which previously raced the recorder's stop() flush and
 *  meant short calls lost their only chunk. */
export function ensureOpen(): Promise<void> {
  return open().then(() => undefined);
}

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: ['sessionId', 'callId', 'seq'],
        });
        store.createIndex('by_session', 'sessionId', { unique: false });
        store.createIndex('by_session_call', ['sessionId', 'callId'], { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB open failed'));
  });
  return dbPromise;
}

export async function putChunk(row: ChunkRow): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IDB put failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IDB tx aborted'));
  });
}

/** Mark a chunk as successfully uploaded (eligible for deletion). */
export async function markUploaded(
  sessionId: string,
  callId: string,
  seq: number,
): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get = store.get([sessionId, callId, seq]);
    get.onsuccess = () => {
      const row = get.result as ChunkRow | undefined;
      if (!row) return resolve();
      store.put({ ...row, uploaded: true });
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IDB markUploaded failed'));
  });
}

/** List every chunk we still have for this session across ALL calls. */
export async function listChunks(sessionId: string): Promise<ChunkRow[]> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const idx = store.index('by_session');
    const req = idx.getAll(IDBKeyRange.only(sessionId));
    req.onsuccess = () =>
      resolve(
        (req.result as ChunkRow[]).sort((a, b) => {
          if (a.callId !== b.callId) return a.callStartedAt - b.callStartedAt;
          return a.seq - b.seq;
        }),
      );
    req.onerror = () => reject(req.error ?? new Error('IDB list failed'));
  });
}

/** Drop a chunk completely — after the server has acknowledged AND we've
 *  decided we don't need to keep it around for any other reason. */
export async function deleteChunk(
  sessionId: string,
  callId: string,
  seq: number,
): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete([sessionId, callId, seq]);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IDB delete failed'));
  });
}

/** List every (sessionId) that has at least one chunk on disk — used by
 *  the resumable upload worker to discover orphaned sessions on boot. */
export async function listOrphanSessions(): Promise<string[]> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const idx = tx.objectStore(STORE).index('by_session');
    const seen = new Set<string>();
    const req = idx.openKeyCursor();
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) return resolve([...seen]);
      seen.add(cur.key as string);
      cur.continue();
    };
    req.onerror = () => reject(req.error ?? new Error('IDB list-orphans failed'));
  });
}
